import React, { useState, useEffect } from 'react';
import { getDashboardSummary, getInsights, getDailyMetrics, getTdee } from '../services/api';
import WeightChart from './Charts/WeightChart';
import DeficitChart from './Charts/DeficitChart';
import TrendChart from './Charts/TrendChart';
import WeeklySummary from './Analytics/WeeklySummary';
import Insights from './Analytics/Insights';
import WeightForecast from './Analytics/WeightForecast';
import CollapsibleSection from './CollapsibleSection';
import SyncStatusBanner from './SyncStatusBanner';

function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [recentMetrics, setRecentMetrics] = useState([]);
  const [defizitCheck, setDefizitCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(90); // Default 90 Tage

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  useEffect(() => {
    const loadDefizitCheck = async () => {
      try {
        const data = await getTdee(30);
        setDefizitCheck(data);
      } catch (err) {
        console.error('Error loading Defizit-Check:', err);
      }
    };
    loadDefizitCheck();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const today = new Date();
      const startDate = new Date(today);

      // 0 = alle Daten (ab 2020)
      if (timeRange === 0) {
        startDate.setFullYear(2020, 0, 1);
      } else {
        startDate.setDate(today.getDate() - timeRange);
      }

      const [summaryData, insightsData, metricsData] = await Promise.all([
        getDashboardSummary(),
        getInsights(),
        getDailyMetrics({
          start_date: startDate.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
          limit: timeRange === 0 ? 2000 : timeRange + 10
        })
      ]);

      setSummary(summaryData);
      setInsights(insightsData);
      setRecentMetrics(metricsData);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="spinner"></div>
        <p>Lade Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard error">
        <h2>Fehler beim Laden</h2>
        <p>{error}</p>
        <button onClick={loadDashboardData}>Erneut versuchen</button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <div className="time-range-selector">
          <label>Zeitraum: </label>
          <select value={timeRange} onChange={(e) => setTimeRange(Number(e.target.value))}>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
            <option value={180}>6 Monate</option>
            <option value={365}>1 Jahr</option>
            <option value={0}>Alle Daten</option>
          </select>
          <button
            className="refresh-btn"
            onClick={loadDashboardData}
            title="Daten aktualisieren"
          >
            ↻
          </button>
        </div>
      </div>
      <SyncStatusBanner />

      {/* Summary Cards */}
      <CollapsibleSection id="dashboard-summary" title="Übersicht">
        <div className="summary-cards">
          <div className="card card-weight">
            <h3>Aktuelles Gewicht</h3>
            <p className="value">{summary?.current_weight ? `${summary.current_weight} kg` : 'N/A'}</p>
            <p className={`change ${summary?.weight_change_7d < 0 ? 'positive' : summary?.weight_change_7d > 0 ? 'negative' : ''}`}>
              {summary?.weight_change_7d !== null && summary?.weight_change_7d !== undefined
                ? `${summary.weight_change_7d > 0 ? '+' : ''}${Number(summary.weight_change_7d).toFixed(2)} kg (7 Tage)`
                : 'Keine Daten'}
            </p>
          </div>

          <div className="card card-deficit">
            <h3>Ø Defizit (7 Tage)</h3>
            <p className="value">{summary?.avg_deficit_7d ? `${summary.avg_deficit_7d} kcal` : 'N/A'}</p>
            <p className="sub">Erwartet: {summary?.expected_loss_7d ? `${Number(summary.expected_loss_7d).toFixed(2)} kg` : 'N/A'}</p>
          </div>

          <div className="card card-data">
            <h3>Datensätze</h3>
            <p className="value">{recentMetrics.length}</p>
            <p className="sub">im gewählten Zeitraum</p>
          </div>

          {/* Defizit-Check Card */}
          <div className="card card-accuracy">
            <h3>Defizit-Check</h3>
            {defizitCheck && defizitCheck.current &&
             defizitCheck.current.real_deficit_per_day != null &&
             defizitCheck.current.assumed_deficit_per_day != null ? (() => {
              const real = defizitCheck.current.real_deficit_per_day;
              const assumed = defizitCheck.current.assumed_deficit_per_day;
              const gap = assumed - real;
              const gapPct = real !== 0 ? Math.round((gap / Math.abs(real)) * 100) : 0;
              const isOver = real > 0 && gap > Math.abs(real) * 0.2;
              const isUnder = real > 0 && gap < -Math.abs(real) * 0.2;
              const isGaining = real <= 0;
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', gap: '0.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 600 }}>Tracking</div>
                      <div style={{ fontWeight: 700 }}>−{Math.abs(assumed).toLocaleString('de-DE')}</div>
                    </div>
                    <div style={{ color: 'var(--text-muted)', alignSelf: 'center' }}>→</div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 600 }}>Körper</div>
                      <div style={{ fontWeight: 700 }}>−{Math.abs(real).toLocaleString('de-DE')}</div>
                    </div>
                  </div>
                  <p className={`sub ${isGaining ? '' : isOver ? 'text-error' : isUnder ? 'text-success' : 'text-success'}`}>
                    {isGaining ? '↗ Gewichtszunahme' : isOver ? `⚠ ${gapPct}% Überschätzung` : isUnder ? `↑ ${Math.abs(gapPct)}% Unterschätzung` : '✓ Tracking stimmt'}
                  </p>
                  <p className="detail text-muted">letzte 30 Tage · {defizitCheck.current.tracking_days} Tage Daten</p>
                </>
              );
            })() : (
              <p className="sub">Nicht genug Daten</p>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Insights */}
      {insights && insights.length > 0 && (
        <CollapsibleSection id="dashboard-insights" title="Insights">
          <Insights insights={insights} />
        </CollapsibleSection>
      )}

      {/* Charts */}
      <CollapsibleSection id="dashboard-charts" title="Charts">
        <div className="charts-grid">
          <div className="chart-container">
            <h3>Gewichtsverlauf ({timeRange === 0 ? 'Alle' : `${timeRange} Tage`})</h3>
            <WeightChart metrics={recentMetrics} />
          </div>

          <div className="chart-container">
            <h3>Kaloriendefizit ({timeRange === 0 ? 'Alle' : `${timeRange} Tage`})</h3>
            <DeficitChart metrics={recentMetrics} />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection id="dashboard-trends" title="Wöchentliche Trends">
        <div className="chart-container-full">
          <TrendChart />
        </div>
      </CollapsibleSection>

      <CollapsibleSection id="dashboard-weekly" title="Wochenübersicht">
        <WeeklySummary />
      </CollapsibleSection>

      <CollapsibleSection id="dashboard-forecast" title="Gewichtsprognose">
        <WeightForecast />
      </CollapsibleSection>
    </div>
  );
}

export default Dashboard;
